
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch,
  watchEffect,
} from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import Step1 from "@/views/pages/application/research/steps/Step1.vue";
import Step2 from "@/views/pages/application/research/steps/Step2.vue";
import Step3 from "@/views/pages/application/research/steps/Step3.vue";
import Step4 from "@/views/pages/application/research/steps/Step4.vue";
import Step5 from "@/views/pages/application/research/steps/Step5.vue";
import Step6 from "@/views/pages/application/research/steps/Step6.vue";
import { StepperComponent } from "@/assets/ts/components";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import * as Yup from "yup";
import { useForm } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { Actions } from "@/store/enums/StoreEnums";
import router from "@/router";
import { useToast } from "vue-toastification";
import NoteSection from "@/components/sections/NoteSection.vue";

interface IStep1 {
  p1: string;
  p2: string;
  p3: string;
  p4: string;
  p5: string;
  p6: string;
  p7: string;
  fundingAgency: string;
}

interface IStep2 {
  duration: string;
  startDate: Date;
  endDate: Date;
}

interface IStep3 {
  agencyType: string;
  agency: string;
  agencyOther: string;
  isRequiredGrant: string;
}

interface IStep4 {
  isUtilizeAcademicRecord: string;
  allAcademicRecords: string;
  isReviewed: string;
  reviewerName: string;
  approvalLetter: string;
}

interface IStep5 {
  hasConflict: string;
  investigatorId: string;
  description: string;
  coiDescription: string;
}

interface CreateAccount extends IStep1, IStep2, IStep3, IStep4, IStep5 {}

export default defineComponent({
  name: "kt-vertical-wizard",
  components: {
    Step1,
    Step2,
    Step3,
    Step4,
    Step5,
    Step6,
    NoteSection,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const toast = useToast();

    const _stepperObj = ref<StepperComponent | null>(null);
    const verticalWizardRef = ref<HTMLElement | null>(null);
    const currentStepIndex = ref(1);
    const jumped = ref(0);
    const fromJumpled = ref(0);
    const previewStep = ref(0);

    const currentResearch = computed(() => {
      return store.getters.currentResearch;
    });

    let formData = ref<CreateAccount>({
      p1:
        currentResearch.value.data && currentResearch.value.data.researchProposals != null && currentResearch.value.data.researchProposals.length >1 &&
        currentResearch.value.data.researchProposals[1].isChecked
          ? "1"
          : "0",
      p2:
        currentResearch.value.data && currentResearch.value.data.researchProposals != null && currentResearch.value.data.researchProposals.length >2 &&
        currentResearch.value.data.researchProposals[2].isChecked
          ? "1"
          : "0",
      p3:
        currentResearch.value.data && currentResearch.value.data.researchProposals != null && currentResearch.value.data.researchProposals.length >3 &&
        currentResearch.value.data.researchProposals[3].isChecked
          ? "1"
          : "0",
      p4:
        currentResearch.value.data && currentResearch.value.data.researchProposals != null && currentResearch.value.data.researchProposals.length >4 &&
        currentResearch.value.data.researchProposals[4].isChecked
          ? "1"
          : "0",
      p5:
        currentResearch.value.data && currentResearch.value.data.researchProposals != null && currentResearch.value.data.researchProposals.length >5 &&
        currentResearch.value.data.researchProposals[5].isChecked
          ? "1"
          : "0",
      p6:
        currentResearch.value.data && currentResearch.value.data.researchProposals != null && currentResearch.value.data.researchProposals.length >6 &&
        currentResearch.value.data.researchProposals[6].isChecked
          ? "1"
          : "0",
      p7:
        currentResearch.value.data && currentResearch.value.data.researchProposals != null && currentResearch.value.data.researchProposals.length >7 &&
        currentResearch.value.data.researchProposals[7].isChecked
          ? "1"
          : "0",
      fundingAgency: currentResearch.value.data
        ? currentResearch.value.data.fundingAgency
        : "",
      duration: currentResearch.value?.data
        ? currentResearch.value.data?.duration
        : "",
      startDate: currentResearch.value.data
        ? currentResearch.value.data.startDate
        : new Date(),
      endDate: currentResearch.value.data
        ? currentResearch.value.data.endDate
        : new Date(),
      agencyType: currentResearch.value?.data?.agencyType
        ? currentResearch.value.data.agencyType
        : "",
      agency: currentResearch.value.data
        ? currentResearch.value.data.agency
        : "",
      agencyOther: currentResearch.value.data
        ? currentResearch.value.data.agencyOther
        : "",
      isRequiredGrant:
        currentResearch.value.data && currentResearch.value.data.isRequiredGrant
          ? "1"
          : "0",
      isUtilizeAcademicRecord:
        currentResearch.value.data &&
        currentResearch.value.data.isUtilizeAcademicRecord
          ? "1"
          : "0",
      allAcademicRecords: currentResearch.value.data
        ? currentResearch.value.data.allAcademicRecords
        : "",
      isReviewed:
        currentResearch.value.data && currentResearch.value.data.isReviewed
          ? "1"
          : "0",
      reviewerName: currentResearch.value.data
        ? currentResearch.value.data.reviewerName
        : "",
      approvalLetter: currentResearch.value.data
        ? currentResearch.value.data.approvalLetter
        : "",
      description: currentResearch.value.data
        ? currentResearch.value.data.description
        : "",
      hasConflict:
        currentResearch.value.data && currentResearch.value.data.hasConflict
          ? "1"
          : "0",
      investigatorId: currentResearch.value.data
        ? currentResearch.value.data.investigatorId
        : "",
      coiDescription: currentResearch.value.data
        ? currentResearch.value.data.coiDescription
        : "",
    });

    onMounted(() => {
      store.dispatch(Actions.GET_IRB_APPLICATION_RESEARCH, route.params.id);

      _stepperObj.value = StepperComponent.createInsance(
        verticalWizardRef.value as HTMLElement
      );

      setCurrentPageBreadcrumbs("Vertical", ["Pages", "Wizards"]);
    });

    const createAccountSchema = [
      Yup.object()
        .shape({
          p1: Yup.string(),
          p2: Yup.string(),
          p3: Yup.string(),
          p4: Yup.string(),
          p5: Yup.string(),
          p6: Yup.string(),
          p7: Yup.string(),
          fundingAgency: Yup.string().when("p7", {
            is: "1",
            then: Yup.string().required().label("Existing"),
          }),
        })
        .test("researchProposals", "", function (value) {
          const checkedCount = Object.values(value).some((val) => val === "1");
          return (
            checkedCount ||
            this.createError({
              path: "researchProposals",
              message: "At least one category must be selected",
            })
          );
        }),
      Yup.object({
        duration: Yup.string().required().label("Duration"),
      }),
      Yup.object({
        agencyType: Yup.string().required().label("Agency type"),
        agency: Yup.string().when("agencyType", {
          is: (value) => {
            return value === "External Agency" || value === "Internal Agency";
          },
          then: Yup.string()
            .required()
            .label(
              "Agency"
            ),
        }),
        agencyOther: Yup.string().when("agency", {
          is: (value) => {
            return (
              value === "State Agency" ||
              value === "Company" ||
              value === "Organization" ||
              value === "Others"
            );
          },
          then: Yup.string().required().label("Please specify"),
        }),
        isRequiredGrant: Yup.string(),
      }),
      Yup.object({
        isUtilizeAcademicRecord: Yup.string(),
        allAcademicRecords: Yup.string().when("isUtilizeAcademicRecord", {
          is: "1",
          then: Yup.string().required().label("All academic records"),
        }),
        isReviewed: Yup.string(),
        reviewerName: Yup.string().when("isReviewed", {
          is: "1",
          then: Yup.string().required().label("Name of the institution"),
        }),
      }),
      Yup.object({
        hasConflict: Yup.string(),
        investigatorId: Yup.string().when("hasConflict", {
          is: "1",
          then: Yup.string().required().label("Investigator"),
        }),
        description: Yup.string().when("hasConflict", {
          is: "1",
          then: Yup.string()
            .required()
            .label("Category of conflict of interest"),
        }),
        coiDescription: Yup.string().when("hasConflict", {
          is: "1",
          then: Yup.string().required().label("Conflict of interest"),
        }),
      }),
    ];

    const currentSchema = computed(() => {
      return createAccountSchema[currentStepIndex.value - 1];
    });

    const { resetForm, handleSubmit } = useForm<
      IStep1 | IStep2 | IStep3 | IStep4 | IStep5
    >({
      validationSchema: currentSchema,
    });

    const totalSteps = computed(() => {
      if (!_stepperObj.value) {
        return;
      }

      return _stepperObj.value.totatStepsNumber;
    });

    resetForm({
      values: {
        ...formData.value,
      },
    });

    const handleStep = handleSubmit((values) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      formData.value = {
        ...formData.value,
        ...values,
      };

      let data = currentResearch.value.data;
      const stepToUpdate =
        jumped.value === 1
          ? previewStep.value
          : fromJumpled.value == 0
          ? currentStepIndex.value - 1
          : currentStepIndex.value;

      if (stepToUpdate === 0) {
        if (data.researchProposals == null) {
           data.researchProposals = [];
        }
        for (var i = 0; i < 8 ; i++) {
          data.researchProposals[i] = {value: i, isChecked: false};
        }
        data.researchProposals[0].isChecked = false;
        data.researchProposals[1].isChecked = formData.value.p1 == "1";
        data.researchProposals[2].isChecked = formData.value.p2 == "1";
        data.researchProposals[3].isChecked = formData.value.p3 == "1";
        data.researchProposals[4].isChecked = formData.value.p4 == "1";
        data.researchProposals[5].isChecked = formData.value.p5 == "1";
        data.researchProposals[6].isChecked = formData.value.p6 == "1";
        data.researchProposals[7].isChecked = formData.value.p7 == "1";
        if (formData.value.p7 === "1") {
          data.fundingAgency = formData.value.fundingAgency;
        } else {
          data.fundingAgency = "";
        }
      } else if (stepToUpdate === 1) {
        data.duration = formData.value.duration;
        data.startDate = formData.value.startDate;
        data.endDate = formData.value.endDate;
      } else if (stepToUpdate === 2) {
        data.agencyType = formData.value.agencyType;
        data.agency = formData.value.agency;
        data.agencyOther = formData.value.agencyOther;
        data.isRequiredGrant = formData.value.isRequiredGrant == "1";
      } else if (stepToUpdate === 3) {
        data.isUtilizeAcademicRecord =
          formData.value.isUtilizeAcademicRecord == "1";
        data.allAcademicRecords =
          formData.value.isUtilizeAcademicRecord === "1"
            ? formData.value.allAcademicRecords
            : "";
        data.isReviewed = formData.value.isReviewed == "1";
        data.reviewerName =
          formData.value.isReviewed == "1" ? formData.value.reviewerName : "";
        data.approvalLetter =
          formData.value.isReviewed == "1" ? formData.value.approvalLetter : "";
      } else if (stepToUpdate === 4) {
        data.description = formData.value.description;
        data.hasConflict = formData.value.hasConflict == "1";
        data.investigatorId =
          formData.value.hasConflict == "1"
            ? formData.value.investigatorId
            : "";
        data.coiDescription =
          formData.value.hasConflict == "1"
            ? formData.value.coiDescription
            : "";
      }

      jumped.value = 0;
      fromJumpled.value = 0;
      if ([0, 1, 2, 3, 4, 5].indexOf(currentStepIndex.value) > -1) {
        const json = JSON.stringify(data);
        store
          .dispatch(Actions.POST_IRB_RESEARCH, json)
          .then(() => {
            if (currentStepIndex.value === 5) {

              toast.success("Information Saved", {
                timeout: 2000
              });
              router.push({
                  name: "edit-research-activities",
                  params: { id: route.params.id },
                });
              
            }
            currentStepIndex.value++;
            if (!_stepperObj.value) {
              return;
            }
            //_stepperObj.value.goNext();
            _stepperObj.value?.goto(currentStepIndex.value);
          })
          .catch(() => {
            toast.error(store.getters.getIrbError, {
                timeout: 2000
              });
            
          });
      }
    });

    const previousStep = () => {
      if (!_stepperObj.value) {
        return;
      }

      currentStepIndex.value--;

      _stepperObj.value.goPrev();
    };

    const jumpToStep = (step) => {
      jumped.value = 0;
      fromJumpled.value = 1;
      previewStep.value = currentStepIndex.value - 1;

      if (step + 1 !== currentStepIndex.value) {
        jumped.value = 1;
      }
      // console.log("goto step");
      currentStepIndex.value = step;
      document.getElementById("buttonSubmit")?.click();
      /*if (step !== currentStepIndex.value) {
        console.log("goto step");
        currentStepIndex.value = step;
        _stepperObj.value?.goto(step + 1);
      }*/
    };

    const formSubmit = () => {
      toast.success("Information Saved", {
                timeout: 2000
              });
              window.location.reload();
      
    };

    watchEffect(() => {
      const data = store.getters.currentResearch;
      if (data === null || data.data === undefined) return;
      // if (!_stepperObj.value) {
      //   return;
      // }

      // currentStepIndex.value = 2;

      // _stepperObj.value.goto(2);
    });

    watch(
      () => route.params.id,
      async () => {
        await store.dispatch(
          Actions.GET_IRB_APPLICATION_RESEARCH,
          route.params.id
        );
      }
    );

    return {
      formData,
      verticalWizardRef,
      jumpToStep,
      previousStep,
      handleStep,
      formSubmit,
      totalSteps,
      currentStepIndex,
    };
  },
});
